import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import RevolutCheckout from '@revolut/checkout';
import css from './RevolutForm.module.css';
import { FormattedMessage, FormattedDate, useIntl } from 'react-intl';
import { subtractTime } from '../../../util/dates';
import { ExternalLink } from '../../../components';
import { errorLogger } from '../../../util/error.logger';
import { BOOKING_EVENTS } from '../../../util/bookingProgress';
import { clearRevolutOrder } from './revolutOrderCache';
import {
  formatPriceFromUnitPurchase,
  formatPriceFromUnitPurchaseNoDecimals,
} from '../../../util/currency';
import { useSelector } from 'react-redux';

const iconSecure =
  'https://res.cloudinary.com/dlcacck3a/image/upload/v1769082566/socialbnb/assets/icons/greensecure.webp';

const CheckoutForm = ({
  data,
  publicId,
  revolutOrderId,
  mode,
  locale,
  listingId,
  userId,
}) => {
  const {
    isSecondPayment,
    isEarlyExperienceBooking,
    callbackFunction,
    termsAndConditions,
    amount,
    basePrice,
    transaction,
    transitionName,
    startDate,
    endDate,
    seats,
    guests,
    isGiftCard,
    trackPayment,
    orderCacheKey,
  } = data;

  const intl = useIntl();
  const [errorMessage, setErrorMessage] = useState();
  const [loading, setLoading] = useState(false);
  const [travelRightsAccepted, setTravelRightsAccepted] = useState(false);
  const [instanceReady, setInstanceReady] = useState(false);
  const reduxUser = useSelector(state => state?.user?.currentUser?.attributes);

  const customerEmail = reduxUser?.email
  const customerName = reduxUser?.profile?.firstName + ' ' + reduxUser?.profile?.lastName;

  const paymentRequestRef = useRef(null);
  const instanceRef = useRef(null);

  // Keep callback in a ref so the Revolut onSuccess closure always calls the latest version
  const callbackRef = useRef(callbackFunction);
  useEffect(() => {
    callbackRef.current = callbackFunction;
  }, [callbackFunction]);

  const dataToolTip = intl.formatMessage({ id: 'checkoutPage.acceptTocTooltip' });
  const isExperienceType =
    transaction?.listing?.attributes?.publicData?.listingType == 'impact-experience';
  const finalListingId = listingId || transaction?.listingId?.uuid;
  const finalUserId = userId || transaction?.customer?.id?.uuid;

  const handleError = err => {
    setLoading(false);
    setErrorMessage(err.message);
    errorLogger(err, {
      event: 'revolut-payment',
      file: 'RevolutForm/CheckoutForm.js',
      listingId: finalListingId,
      userId: finalUserId,
      startDate,
      endDate,
      seats,
      guests,
    });
    trackPayment?.(BOOKING_EVENTS.PAYMENT_FAILED, {
      payment: { orderId: revolutOrderId },
      error: { step: 'revolut-payment', code: err?.name, message: err?.message },
    });
  };

  const handleCancel = () => {
    setLoading(false);
    trackPayment?.(BOOKING_EVENTS.PAYMENT_CANCELLED, {
      payment: { orderId: revolutOrderId },
      error: {
        step: 'revolut-onCancel',
        code: 'CUSTOMER_CANCELLED',
        message: 'The customer closed the card payment window before completing the payment',
      },
    });
  };

  const onPaymentSuccess = async () => {
    const protectedDataValues = {
      revolut: {
        orderId: revolutOrderId,
      },
      bookingSurveyEnabled: true,
      totalAmount: Math.round(basePrice * 100),
    };
    setLoading(false);

    // This order is paid now — drop it from the shared cache so a later
    // checkout for the same listing/amount creates a fresh order.
    if (orderCacheKey) {
      clearRevolutOrder(orderCacheKey);
    }

    // Revolut has taken the payment by this point, so the record has to say
    // "paid" even if creating the marketplace transaction below fails.
    trackPayment?.(BOOKING_EVENTS.PAYMENT_AUTHORIZED, {
      payment: { orderId: revolutOrderId },
    });

    try {
      await callbackRef.current(protectedDataValues);
    } catch (e) {
      errorLogger(e, {
        event: 'revolut-callbackFunction-failed',
        file: 'RevolutForm/RevolutForm.js',
        isSecondPayment,
        isEarlyExperienceBooking,
        amount,
        basePrice,
        transactionId: transaction?.id?.uuid,
        transitionName,
        listingId: finalListingId,
        userId: finalUserId,
        startDate,
        endDate,
        seats,
        guests,
      });
    }
  };

  useEffect(() => {
    if (!publicId) return;

    let destroyed = false;

    RevolutCheckout(publicId, mode)
      .then(instance => {
        if (destroyed) {
          instance.destroy();
          return;
        }
        instanceRef.current = instance;
        setInstanceReady(true);

        if (paymentRequestRef.current) {
          instance.paymentRequest({
            target: paymentRequestRef.current,
            onSuccess: () => {
              if (!destroyed) onPaymentSuccess();
            },
            onError: err => {
              if (!destroyed) handleError(err);
            },
            onCancel: () => {
              if (!destroyed) handleCancel();
            },
          });
        }
      })
      .catch(err => {
        console.error('Error initialising Revolut Checkout:', err);
        handleError(err);
      });

    return () => {
      destroyed = true;
      if (instanceRef.current) {
        instanceRef.current.destroy();
        instanceRef.current = null;
      }
    };
  }, [publicId, mode, locale]);

  const handlePayment = event => {
    event.preventDefault();
    if (!instanceRef.current || !instanceReady) return;

    setLoading(true);
    setErrorMessage('');

    instanceRef.current.payWithPopup({
      name: customerName,
      email: customerEmail,
      locale: locale,
      onSuccess: () => {
        onPaymentSuccess();
      },
      onError: err => {
        handleError(err);
      },
      onCancel: () => {
        handleCancel();
      },
    });
  };

  const packagedTravelRightsLink = (
    <ExternalLink href={intl.formatMessage({ id: 'CheckoutPage.packagedTravelRightsDoc' })}>
      <FormattedMessage id="CheckoutPage.packagedTravelRightsDocName" />
    </ExternalLink>
  );

  const isPaymentEnabled = isGiftCard
    ? termsAndConditions?.termsAccepted
    : travelRightsAccepted && termsAndConditions?.termsAccepted;

  return (
    <div>
      {isGiftCard ? (
        <div className={css.earlyBookingMessage}>
          <span className={css.dueNowTitle}>
            <FormattedMessage id="giftcardPage.payment" />{' '}
            <span style={{ fontWeight: '600' }}>{Math.round(Number(amount / 100))}€</span>
          </span>
        </div>
      ) : isEarlyExperienceBooking ? (
        <div className={css.earlyBookingMessage}>
          <span className={css.dueNowTitle}>
            <FormattedMessage id="PaymentForm.amount.pay.now" />
            {Math.round(Number(amount))}€
          </span>
          <span className={css.prepaymentMessage}>
            <FormattedMessage id="PaymentForm.prepaymentMessage" />
          </span>
        </div>
      ) : isSecondPayment ? (
        <div className={css.earlyBookingMessage}>
          <span className={css.dueNowTitle}>
            <FormattedMessage id="PaymentForm.amount.pay.now.noDots" />
          </span>
          <div className={css.priceTitle}>
            <FormattedMessage id="OrderBreakdown.remainingAmount" />
            <span style={{ fontWeight: '600' }}>{Math.round(Number(amount / 100))}€</span>:
          </div>
        </div>
      ) : (
        <div
          className={css.earlyBookingMessage}
          style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}
        >
          <span className={css.dueNowTitle}>
            <FormattedMessage id="PaymentForm.amount.pay.now" />
          </span>
          <span style={{ fontWeight: '600' }}>
            {isExperienceType
              ? formatPriceFromUnitPurchase(intl, amount * 100)
              : `${formatPriceFromUnitPurchaseNoDecimals(intl, amount * 100, '€')}€`}{' '}
          </span>
        </div>
      )}

      {!isGiftCard && (
        <div className={css.rights}>
          <input
            type="checkbox"
            id="rights"
            checked={travelRightsAccepted}
            onChange={() => setTravelRightsAccepted(!travelRightsAccepted)}
            data-elbaction="click:click travelRights"
          />
          <label htmlFor="rights">
            {intl.formatMessage(
              { id: 'CheckoutPage.packagedTravelRightsText' },
              { packagedTravelRightsLink }
            )}
          </label>
        </div>
      )}
      {termsAndConditions?.component}

      <div className={css.paymentFormTitle}>
        <span className={css.paymentFormTitleText}>
          <FormattedMessage id="PaymentForm.choose.payment.option" />
        </span>
        <ul className={css.securePayment}>
          {(() => {
            const cancellationScope = isExperienceType ? 'experience' : 'stay';
            const getCancellationDate = () => {
              const startDateValue = startDate || transaction?.booking?.attributes?.start;
              if (!startDateValue) return null;
              const today = new Date();
              const isLessThan30Days = subtractTime(startDateValue, 30, 'days') < today;
              return !isLessThan30Days ? subtractTime(startDateValue, 30, 'days') : null;
            };
            const cancellationDate = getCancellationDate();
            const showFreeCancellation =
              isExperienceType ? isEarlyExperienceBooking : !!cancellationDate;

            if (!showFreeCancellation) return null;

            return (
              <li className="flex items-center gap-2">
                <img src={iconSecure} alt="Secure" className={css.secureIcon} />
                <span className={css.secureText}>
                  {cancellationDate ? (
                    <>
                      <FormattedMessage
                        id={`LineItemDescription.${cancellationScope}.freeCancellationBefore`}
                      />
                      <FormattedDate value={cancellationDate} day="numeric" month="long" />
                    </>
                  ) : (
                    <FormattedMessage
                      id={`LineItemDescription.${cancellationScope}.freeCancellationBeforeDate`}
                    />
                  )}
                </span>
              </li>
            );
          })()}
          <li className="flex items-center gap-2">
            <img src={iconSecure} alt="Secure" className={css.secureIcon} />
            <span className={css.secureText}>
              <FormattedMessage id="PaymentForm.paymentIs.secure" />
            </span>
          </li>
        </ul>
      </div>

      <div className={css.formContainer}>
        {!isPaymentEnabled && <div className={css.formOverlay} data-tooltip={dataToolTip} />}
        <form onSubmit={handlePayment}>
          <button
            type="submit"
            className={css.payButton}
            disabled={!instanceReady || loading || !isPaymentEnabled}
            data-elbaction="click:click exp-payButton"
          >
            {loading ? (
              <FormattedMessage id="RevolutForm.processing" />
            ) : (
              <FormattedMessage id="RevolutForm.payWithCard" />
            )}
          </button>
          <div ref={paymentRequestRef} />
          {errorMessage && <div className={css.error}>{errorMessage}</div>}
        </form>
      </div>
    </div>
  );
};

CheckoutForm.propTypes = {
  data: PropTypes.shape({
    isSecondPayment: PropTypes.bool.isRequired,
    termsAndConditions: PropTypes.shape({
      component: PropTypes.node,
      termsAccepted: PropTypes.bool,
    }).isRequired,
    transaction: PropTypes.object,
    transitionName: PropTypes.string,
  }).isRequired,
  publicId: PropTypes.string.isRequired,
  revolutOrderId: PropTypes.string.isRequired,
  mode: PropTypes.string,
  locale: PropTypes.string,
  listingId: PropTypes.string,
  userId: PropTypes.string,
};

export default CheckoutForm;
